import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import PageWrapper from '../../components/PageWrapper';
import AnimatedSection from '../../components/AnimatedSection';
import {
  FiFilter,
  FiArrowRight,
  FiCalendar,
  FiMapPin,
  FiTrendingUp,
  FiBarChart,
  FiCheckCircle
} from 'react-icons/fi';
import RmbIcon from '../../components/RmbIcon';
import './Projects.css';

const Projects = () => {
  const [filter, setFilter] = useState('all');

  // 容器动画 variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const projects = useMemo(() => ([
    {
      id: 1,
      title: '宁波国际科创中心 · 全过程造价治理',
      type: '工程造价',
      category: 'cost',
      industry: '科技园区',
      location: '宁波 · 高新区',
      date: '2024年',
      budget: '28亿',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80&fm=webp',
      summary:
        '项目包含科研办公、实验中心、公共配套三大功能组团。识道提供投资决策、限额设计、动态成本控制、结算审核等全过程造价咨询服务。',
      results: [
        '动态成本偏差控制在 3% 以内，审减金额 2.6 亿元',
        '建立"目标成本-合同成本-实际成本"三算对比模型并可视化展示',
        '通过AI识别并纠偏 72 项变更签证，项目提前 25 天竣工'
      ]
    },
    {
      id: 2,
      title: '宁波轨道交通枢纽中心 · 概预算结算审计',
      type: '概预算审核',
      category: 'budget',
      industry: '交通基建',
      location: '宁波 · 鄞州区',
      date: '2023年',
      budget: '45亿',
      image: 'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=800&q=80&fm=webp',
      summary:
        '集轨道交通、综合交通换乘、商业配套于一体的大型枢纽工程。识道为财政审计部门提供概算、预算、结算全流程审计服务。',
      results: [
        'AI+专家双审识别成本偏差事项 134 项，审减金额 5.8 亿元',
        '形成 1800+ 页审计底稿与整改清单，满足财政审计一体化要求',
        '审核效率较传统模式提升 60%，通过财政、审计"联合验收"'
      ]
    },
    {
      id: 3,
      title: '宁波智慧制造产业园 · 电子化招投标',
      type: '招投标',
      category: 'tendering',
      industry: '工业制造',
      location: '浙江 · 宁波',
      date: '2024年',
      budget: '36亿',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&q=80&fm=webp',
      summary:
        '项目建设智能制造厂房、研发孵化中心、公共配套。识道承担 EPC 总承包招标代理及投标策略制定，全流程电子化留痕。',
      results: [
        '覆盖 9 大专业 32 个子项目招标，参与投标单位 120+',
        '采用"技术标+方案标+商务标"综合评审模型，中标价较控制价降低 11.8%',
        '招标过程合规率 100%，留痕材料一次性通过审计'
      ]
    },
    {
      id: 4,
      title: '浙江智慧医院集群 · BIM+造价一体化',
      type: '工程造价',
      category: 'cost',
      industry: '医疗健康',
      location: '宁波 · 杭州 · 温州',
      date: '2023年',
      budget: '18亿',
      image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=800&q=80&fm=webp',
      summary:
        '三座智慧医院同步建设，采用 BIM 正向设计 + 造价一体化出量模式，形成标准化造价数据库并沉淀指标。',
      results: [
        'BIM 直接出量覆盖率提升至 92%，设计变更联动时间缩短 70%',
        '沉淀 600+ 项材料设备价格，建立集团造价指标库',
        '项目整体投资控制率 15%，建设周期缩短 3 个月'
      ]
    },
    {
      id: 5,
      title: '西部新能源基地 · 造价审计与投融资决策',
      type: '概预算审核',
      category: 'budget',
      industry: '新能源',
      location: '甘肃 · 酒泉',
      date: '2022年',
      budget: '52亿',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800&q=80&fm=webp',
      summary:
        '风光储一体化新能源基地项目，对 EPC 合同、设备采购、安装调试进行审计，支持国有资本投资决策。',
      results: [
        '审计金额 52 亿元，识别材料采购异常 36 项，优化资金计划 12%',
        '形成新能源行业造价指标模型，为后续 8 个项目提供参考',
        '通过能源、水利、财政三部门联合审查'
      ]
    },
    {
      id: 6,
      title: '宁波轨道 TOD 综合开发 · 招投标 + 造价一体化',
      type: '招投标',
      category: 'tendering',
      industry: 'TOD 综合体',
      location: '宁波 · 北仑区',
      date: '2022年',
      budget: '40亿',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&q=80&fm=webp',
      summary:
        '轨道交通上盖 TOD 综合体开发项目，识道提供招投标组织、投标咨询及全过程造价服务。',
      results: [
        '招投标全程电子化，资料留痕 100%',
        '协助开发商优化商业组合与投资收益模型',
        '项目整体 IRR 提升 2.6%，通过集团投资决策会审'
      ]
    }
  ]), []);

  const filters = [
    { id: 'all', label: '全部项目' },
    { id: 'cost', label: '全过程造价' },
    { id: 'budget', label: '概预算结算审核' },
    { id: 'tendering', label: '招投标代理' }
  ];

  const stats = [
    { value: '1200+', label: '累计项目经验', desc: '覆盖市政、房建、交通、能源等 20+ 行业' },
    { value: '500亿+', label: '累计服务金额', desc: '央国企、政府、上市公司长期合作' },
    { value: '18%', label: '平均审减率', desc: 'AI+专家双重审核，成果可量化' },
    { value: '92%', label: '客户续约率', desc: '以结果导向与服务口碑赢得信任' }
  ];

  const filteredProjects = filter === 'all' ? projects : projects.filter((p) => p.category === filter);

  return (
    <PageWrapper className="projects-page">
      <AnimatedSection className="projects-hero">
        <div className="container">
          <div className="hero-content">
            <motion.div className="hero-copy" variants={itemVariants}>
              <span className="hero-eyebrow">PROJECT PORTFOLIO · 宁波</span>
              <h1>深耕宁波及长三角，服务 1200+ 成功项目</h1>
              <p>
                立足宁波，覆盖浙江全省及长三角区域。我们以"三大服务体系 + 数智平台"为核心，为本地政府、央国企及民企打造可复用的造价治理方法论。
                以下案例涵盖全过程造价咨询、概预算结算审核、招投标代理等典型场景。
              </p>
            </motion.div>
            <motion.div
              className="hero-metrics"
              variants={itemVariants}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="metric-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <span className="metric-value">{stat.value}</span>
                  <span className="metric-label">{stat.label}</span>
                  <p>{stat.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="projects-filter">
        <div className="container">
          <motion.div
            className="filter-bar"
            variants={itemVariants}
          >
            <div className="filter-left">
              <FiFilter className="filter-icon" />
              <span>按服务类型查看案例</span>
            </div>
            <div className="filter-actions">
              {filters.map((f) => (
                <motion.button
                  key={f.id}
                  className={`filter-btn ${filter === f.id ? 'active' : ''}`}
                  onClick={() => setFilter(f.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {f.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="projects-content">
        <div className="container">
          <div className="projects-grid">
            {filteredProjects.map((project, index) => (
              <motion.article
                key={project.id}
                className="project-card"
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
              >
                <div className="project-cover">
                  <div className="project-tags">
                    <span>{project.type}</span>
                    <span>{project.industry}</span>
                  </div>
                  <div className="project-meta">
                    <div><FiMapPin /> {project.location}</div>
                    <div><FiCalendar /> {project.date}</div>
                    <div><RmbIcon size={14} /> {project.budget}</div>
                  </div>
                </div>
                <div className="project-body">
                  <h3>{project.title}</h3>
                  <p className="project-summary">{project.summary}</p>
                  <div className="project-results">
                    {project.results.map((result, idx) => (
                      <div key={idx} className="result-item">
                        <FiCheckCircle />
                        <span>{result}</span>
                      </div>
                    ))}
                  </div>
                  <a href={`/projects/${project.id}`} className="project-link">
                    查看完整案例 <FiArrowRight />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="projects-cta">
        <div className="container">
          <motion.div
            className="cta-card"
            variants={itemVariants}
          >
            <div>
              <h2>需要行业标杆项目数据或案例复盘？</h2>
              <p>
                我们沉淀了 1200+ 项目的造价指标与过程数据，支持同类型项目对标分析、投资测算、造价治理体系搭建。
              </p>
            </div>
            <div className="cta-actions">
              <a href="/contact" className="btn btn-primary">预约案例分享</a>
              <a href="/platform" className="btn btn-secondary">获取数据洞察</a>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>
    </PageWrapper>
  );
};

export default Projects;